import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, EmptyState } from "@/components/common";
import { renderMarkdown } from "@/lib/markdown";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TriangleAlert, Package, TrendingUp, Percent, Clock, Zap, Lightbulb, Sparkles, Loader2 } from "lucide-react";

const ICON = { package: Package, "trending-up": TrendingUp, percent: Percent, clock: Clock, "alert-triangle": TriangleAlert, zap: Zap, lightbulb: Lightbulb };
const STYLE = {
  danger: "border-destructive/30 bg-destructive/5",
  warning: "border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5",
  success: "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5",
  info: "border-primary/30 bg-primary/5",
};
const ICLR = { danger: "text-destructive", warning: "text-[hsl(var(--warning))]", success: "text-[hsl(var(--success))]", info: "text-primary" };

export default function Alerts() {
  const [alerts, setAlerts] = useState(null);
  const [period, setPeriod] = useState("daily");
  const [report, setReport] = useState("");
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => { api.get("/alerts").then((r) => setAlerts(r.data)); }, []);

  const genReport = async (p) => {
    setPeriod(p); setLoadingReport(true); setReport("");
    try { const r = await api.get(`/ai/report?period=${p}`); setReport(r.data.report); }
    catch { setReport("Could not generate report. Please try again."); }
    finally { setLoadingReport(false); }
  };

  if (!alerts) return <Loader label="Scanning your business…" />;

  return (
    <div>
      <PageHeader title="Alerts & AI Reports" subtitle="Unusual situations, opportunities, and your AI briefing." />

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Business Alerts" subtitle={`${alerts.length} signals detected`} testid="alerts-list">
          {alerts.length === 0 ? <EmptyState icon={TriangleAlert} title="All clear" subtitle="No unusual activity" /> : (
            <div className="space-y-3">
              {alerts.map((a, i) => {
                const Icon = ICON[a.icon] || TriangleAlert;
                return (
                  <div key={i} className={`flex gap-3 rounded-lg border p-4 animate-fade-up ${STYLE[a.type]}`} style={{ animationDelay: `${i * 40}ms` }} data-testid={`alert-${i}`}>
                    <div className={`grid place-items-center h-9 w-9 rounded-lg bg-background shrink-0 ${ICLR[a.type]}`}><Icon className="h-4.5 w-4.5" /></div>
                    <div><div className={`font-semibold text-sm ${ICLR[a.type]}`}>{a.title}</div><p className="text-sm text-foreground/80 mt-0.5">{a.message}</p></div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        <SectionCard title="AI Business Report" subtitle="Auto-generated briefing" testid="ai-report"
          action={<Tabs value={period} onValueChange={genReport}><TabsList><TabsTrigger value="daily" data-testid="report-daily">Daily</TabsTrigger><TabsTrigger value="weekly" data-testid="report-weekly">Weekly</TabsTrigger><TabsTrigger value="monthly" data-testid="report-monthly">Monthly</TabsTrigger></TabsList></Tabs>}>
          {!report && !loadingReport && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-primary text-primary-foreground mb-3"><Sparkles className="h-6 w-6" /></div>
              <p className="text-sm text-muted-foreground max-w-xs">Get an AI-written summary of what happened, the main problem, and what to do next.</p>
              <Button onClick={() => genReport(period)} data-testid="generate-report-btn" className="mt-4 font-semibold"><Sparkles className="h-4 w-4 mr-1.5" /> Generate {period} report</Button>
            </div>
          )}
          {loadingReport && <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="h-5 w-5 animate-spin text-primary" /> Analyzing your data…</div>}
          {report && !loadingReport && <div className="space-y-0.5" data-testid="report-content">{renderMarkdown(report)}</div>}
        </SectionCard>
      </div>
    </div>
  );
}
